import { Entity, mapEntity } from '@api';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { WithNAFallback } from '@design/components/table';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { getPriceStatus, isNonZeroReturn } from '@helpers';
import { PriceCellTooltip } from '@terminal/components/price-cell/price-cell-tooltip/PriceCellTooltip';
import { getPriceStamp } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import { useOneDayReturnLivePrice } from '@terminal/hooks/use-one-day-return-live-price/useOneDayReturnLivePrice';
import { priceChangeColors } from '@terminal/utils/common/utils';
import { formatPercentageNumber } from '@terminal/widgets/earnings/utils/utils';

import styles from './CompetitorsSection.module.scss';

export interface ChangeCellProps {
  entity: Entity;
}

export const ChangeCell = ({ entity }: ChangeCellProps) => {
  const {
    oneDayLiveReturn,
    isSubscribing,
    date,
    isLive,
    isLivePriceSupported,
    isExtendedHours,
  } = useOneDayReturnLivePrice(mapEntity(entity));

  if (isSubscribing) {
    return (
      <SkeletonLoader
        className={styles.styledSkeletonLoaderWrapper}
        areas={[
          { styles: { height: '20px', width: '100%', borderRadius: '8px' } },
        ]}
      />
    );
  }

  const status = getPriceStatus(oneDayLiveReturn);
  const stamp = getPriceStamp({
    isLivePriceSupported,
    isLive,
    isDisabled: false,
  });

  return (
    <div className={styles.changeRow}>
      <WithNAFallback
        check={
          isExtendedHours
            ? isNonZeroReturn(oneDayLiveReturn)
            : oneDayLiveReturn !== undefined
        }
        value={
          <Tooltip
            isTouchDevice={false}
            trigger={TooltipTrigger.Hover}
            label={
              date && <PriceCellTooltip type={stamp.stampType} date={date} />
            }
            inPortal
            disabled={!date}
          >
            <span
              className={styles.changeValue}
              style={{ color: priceChangeColors[status] }}
            >
              {formatPercentageNumber({
                value: oneDayLiveReturn,
                includePercentSign: true,
                addPlus: true,
              })}
            </span>
          </Tooltip>
        }
      />
    </div>
  );
};
